/**
 * Helper condivisi per filtri data con operatore (exact/gte/lte/range).
 *
 * Usati da tabella-lavoratori, tabella-imprese, lista-deleghe, elenco-deleghe.
 * Il template NJK corrispondente e' shared-filtro-data.njk.
 */

import { isVoceSemestre, semestreDate } from './semestre-date.ts';

/**
 * Raccoglie filtri data con operatore e converte SEMPRE in formato Da/A.
 * Questo formato e' compatibile con tutti i backend (lavoratori, deleghe, imprese).
 */
export function raccogliDateFilters(root: Element, campi: string[]): Record<string, string> {
  const params: Record<string, string> = {};
  for (const campo of campi) {
    const opEl = root.querySelector(`[data-filtro-op="${campo}"]`) as HTMLSelectElement | null;
    const dateOp = opEl?.value;
    if (!dateOp) continue;

    if (isVoceSemestre(dateOp)) {
      const { da, a } = semestreDate(dateOp);
      params[`${campo}Da`] = da;
      params[`${campo}A`] = a;
      continue;
    }

    const val = (root.querySelector(`[data-filtro-val="${campo}"]`) as HTMLInputElement | null)?.value;
    if (!val) continue;

    if (dateOp === 'exact') {
      params[`${campo}Da`] = val;
      params[`${campo}A`] = val;
    } else if (dateOp === 'gte') {
      params[`${campo}Da`] = val;
    } else if (dateOp === 'lte') {
      params[`${campo}A`] = val;
    } else if (dateOp === 'range') {
      params[`${campo}Da`] = val;
      const valA = (root.querySelector(`[data-filtro-val="${campo}A"]`) as HTMLInputElement | null)?.value;
      if (valA) params[`${campo}A`] = valA;
    }
  }
  return params;
}

/** Con una voce semestre la data non serve: si nasconde e la select prende tutta la riga, altrimenti il testo si taglia */
function aggiornaCampoData(root: Element, sel: HTMLSelectElement): void {
  const semestre = isVoceSemestre(sel.value);
  sel.style.maxWidth = semestre ? 'none' : '130px';
  sel.classList.toggle('rounded-end', semestre);
  root.querySelector(`[data-filtro-val="${sel.dataset.dateOp}"]`)?.classList.toggle('d-none', semestre);
}

/**
 * Handler per toggle show/hide riga range date.
 * Usare con event delegation: delegate.on('change', '[data-date-op]', handler)
 */
export function handleDateOpChange(root: Element, e: Event): void {
  const sel = e.target as HTMLSelectElement;
  if (!sel.dataset?.dateOp) return;
  const campo = sel.dataset.dateOp;
  const rangeEl = root.querySelector(`[data-range="${campo}"]`) as HTMLElement | null;
  if (rangeEl) rangeEl.classList.toggle('d-none', sel.value !== 'range');
  aggiornaCampoData(root, sel);
}

/**
 * Reset: nasconde tutte le righe range date nel componente e ripristina i campi data.
 * Va chiamato dopo il reset del form, perche' legge il valore delle select.
 */
export function resetDateRanges(root: Element): void {
  for (const el of root.querySelectorAll<HTMLElement>('[data-range]')) {
    el.classList.add('d-none');
  }
  for (const sel of root.querySelectorAll<HTMLSelectElement>('[data-date-op]')) {
    aggiornaCampoData(root, sel);
  }
}

/** Numero di filtri attivi: un intervallo di date (campoDa + campoA) conta come uno */
export function contaFiltri(filtri: Record<string, string>): number {
  return new Set(Object.keys(filtri).map(k => k.replace(/(Da|A)$/, ''))).size;
}
